import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack
} from "@chakra-ui/react";
import MainLayout from "@layouts/main.layout";
import { useFirebase } from "context/firebase.context";
import {
  collection,
  count,
  getAggregateFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  sum,
  where
} from "firebase/firestore";
import { useEffect, useState } from "react";

export default function PointsSummaryPage() {
  const { firestore } = useFirebase();
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [recalculatedData, setRecalculatedData] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  // Fetch available months
  useEffect(() => {
    const fetchAvailableMonths = async () => {
      try {
        setLoading(true);
        const summaryRef = collection(firestore, "monthly-creator-point-summary");
        const q = query(summaryRef, orderBy("generatedAt", "desc"), limit(12));
        const querySnapshot = await getDocs(q);
        
        const months = querySnapshot.docs.map(doc => ({
          id: doc.id,
          month: doc.data().month,
          year: doc.data().year,
          monthNumber: doc.data().monthNumber,
          totalCreators: doc.data().totalCreators,
          generatedAt: doc.data().generatedAt
        }));
        
        setAvailableMonths(months);
        if (months.length > 0) {
          setSelectedMonth(months[0].id);
        }
      } catch (err) {
        setError("載入月份列表失敗: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableMonths();
  }, [firestore]);

  // Fetch summary data for selected month
  useEffect(() => {
    if (!selectedMonth) return;

    const fetchSummaryData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const creatorsRef = collection(
          firestore, 
          "monthly-creator-point-summary", 
          selectedMonth, 
          "creators"
        );
        const q = query(creatorsRef, orderBy("totalPoints", "desc"));
        const querySnapshot = await getDocs(q);
        
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setSummaryData(data);
      } catch (err) {
        setError("載入結算資料失敗: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSummaryData();
  }, [selectedMonth, firestore]);

  const formatPoints = (points) => {
    return points.toLocaleString();
  };

  const translateTypeName = (type) => {
    const translations = {
      'CHATROOM_GIFT': '聊天室禮物',
      'CHATROOM_MESSAGE': '聊天室訊息',
      'CHATROOM_UNLOCK_VIDEO': '聊天室影片',
      'CHATROOM_UNLOCK_IMAGE': '聊天室照片',
      'UNLOCK_VIDEO': '影片解鎖',
      'STREAM_GIFT': '直播禮物',
      'WATCH_PRIVATE_STREAM': '私密直播'
    };
    return translations[type] || type;
  };

  const formatMonth = (monthId) => {
    if (!monthId) return "";
    const year = monthId.substring(0, 4);
    const month = monthId.substring(4, 6);
    return `${year}年${parseInt(month)}月`;
  };

  const downloadCSV = () => {
    if (summaryData.length === 0) {
      alert("沒有資料可以下載");
      return;
    }

    // Create CSV content - format: creatorId, displayName, totalPoints, typeBreakdown
    const csvContent = summaryData.map(creator => {
      const typeBreakdown = creator.typeBreakdown || {};
      const typeBreakdownStr = JSON.stringify(typeBreakdown);
      return `${creator.creatorId}, ${creator.displayName}, ${creator.totalPoints}, ${typeBreakdownStr}`;
    }).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `points-${selectedMonth}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getTypeBreakdownTable = (typeBreakdown) => {
    if (!typeBreakdown || Object.keys(typeBreakdown).length === 0) {
      return <Text color="gray.500">無資料</Text>;
    }

    return (
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>類型</Th>
            <Th isNumeric>鑽石數</Th>
          </Tr>
        </Thead>
        <Tbody>
          {Object.entries(typeBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([type, points]) => (
              <Tr key={type}>
                <Td>{translateTypeName(type)}</Td>
                <Td isNumeric fontWeight="medium">{formatPoints(points)}</Td>
              </Tr>
            ))}
        </Tbody>
      </Table>
    );
  };

  const handleCreatorClick = (creator) => {
    setSelectedCreator(creator);
    setRecalculatedData(null);
    
    // Set default dates to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    setIsDialogOpen(true);
  };

  const handleRecalculate = async () => {
    if (!selectedCreator || !startDate || !endDate) {
      alert("請選擇完整的日期範圍");
      return;
    }

    try {
      setRecalculating(true);
      
      // Convert dates to milliseconds
      const start = new Date(startDate + "T00:00:00").getTime();
      const end = new Date(endDate + "T23:59:59").getTime();

      // Query transactions for this creator within date range
      const transactionsRef = collection(firestore, "transactions");
      const q = query(
        transactionsRef,
        where("creatorId", "==", selectedCreator.creatorId),
        where("createdAt", ">=", start),
        where("createdAt", "<=", end),
      );

      // Use aggregate sum instead of iterating
      const aggregateSnapshot = await getAggregateFromServer(q, {
        totalPoints: sum('points'),
        transactionCount: count()
      });
      
      const totalPoints = aggregateSnapshot.data().totalPoints || 0;
      const transactionCount = aggregateSnapshot.data().transactionCount || 0;
      
      // For type breakdown, we still need to fetch documents
      const querySnapshot = await getDocs(q);
      const typeBreakdown = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const points = data.points || 0;
        const type = data.type || "UNKNOWN";
        
        if (!typeBreakdown[type]) {
          typeBreakdown[type] = 0;
        }
        typeBreakdown[type] += points;
      });

      setRecalculatedData({
        totalPoints,
        typeBreakdown,
        transactionCount
      });
    } catch (err) {
      alert("重新計算失敗: " + err.message);
    } finally {
      setRecalculating(false);
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedCreator(null);
    setRecalculatedData(null);
  };

  if (error) {
    return (
      <MainLayout>
        <Box maxW={1200} mx="auto" mt={8} p={4}>
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>錯誤!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box maxW={1200} mx="auto" mt={8} p={4}>
        <VStack spacing={6} align="stretch">
          <Card p={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="2xl" fontWeight="bold" textAlign="center">
                每月主播結算
              </Text>
              
              <HStack spacing={4} align="center" justify="space-between">
                <HStack spacing={4} align="center">
                  <Text fontWeight="semibold">選擇月份:</Text>
                  <Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    maxW="300px"
                    disabled={loading}
                  >
                    {availableMonths.map((month) => (
                      <option key={month.id} value={month.id}>
                        {formatMonth(month.month)} ({month.totalCreators}位創作者)
                      </option>
                    ))}
                  </Select>
                </HStack>
                
                <Button
                  onClick={downloadCSV}
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  disabled={loading || summaryData.length === 0}
                >
                  下載 CSV
                </Button>
              </HStack>
            </VStack>
          </Card>

          {loading ? (
            <Card p={6} textAlign="center">
              <Spinner size="xl" />
              <Text mt={4}>載入中...</Text>
            </Card>
          ) : (
            <Card p={6}>
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="semibold">
                  {selectedMonth ? formatMonth(selectedMonth) : ""} 創作者鑽石結算
                </Text>
                
                {summaryData.length === 0 ? (
                  <Text color="gray.500" textAlign="center" py={8}>
                    該月份無結算資料
                  </Text>
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm" minW="1200px">
                      <Thead>
                        <Tr>
                          <Th w="60px">排名</Th>
                          <Th w="180px">創作者ID</Th>
                          <Th w="120px">顯示名稱</Th>
                          <Th w="100px" isNumeric>總鑽石</Th>
                          <Th w="100px" isNumeric>訂閱收入</Th>
                          <Th w="100px" isNumeric>訂閱佣金</Th>
                          <Th w="100px" isNumeric>訂閱TWD</Th>
                          <Th>鑽石類型明細</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {summaryData.map((creator, index) => (
                          <Tr 
                            key={creator.id}
                            onClick={() => handleCreatorClick(creator)}
                            cursor="pointer"
                            _hover={{ bg: "blue.800" }}
                            transition="background 0.2s"
                          >
                            <Td fontWeight="semibold">{index + 1}</Td>
                            <Td fontFamily="mono" fontSize="sm">{creator.creatorId}</Td>
                            <Td fontWeight="medium">{creator.displayName}</Td>
                            <Td isNumeric fontWeight="bold" color="blue.600">
                              {formatPoints(creator.totalPoints)}
                            </Td>
                            <Td isNumeric>
                              {formatPoints(creator.subscriptionRevenue || 0)}
                            </Td>
                            <Td isNumeric>
                              {formatPoints(creator.subscriptionCommision || 0)}
                            </Td>
                            <Td isNumeric>
                              {formatPoints(creator.subscriptionTwdAmount || 0)}
                            </Td>
                            <Td>
                              {getTypeBreakdownTable(creator.typeBreakdown)}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>
            </Card>
          )}
        </VStack>
      </Box>

      {/* Recalculation Dialog */}
      <Modal isOpen={isDialogOpen} onClose={closeDialog} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>重新計算鑽石區間</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {selectedCreator && (
                <Box p={4} borderRadius="md">
                  <Text fontWeight="bold" fontSize="lg" mb={2}>
                    {selectedCreator.displayName}
                  </Text>
                  <Text fontSize="sm" color="white">
                    Creator ID: {selectedCreator.creatorId}
                  </Text>
                  <Text fontSize="sm" color="white">
                    原始總鑽石數: {formatPoints(selectedCreator.totalPoints)}
                  </Text>
                </Box>
              )}

              <FormControl>
                <FormLabel>開始日期</FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>結束日期</FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </FormControl>

              <Button
                colorScheme="blue"
                onClick={handleRecalculate}
                isLoading={recalculating}
                loadingText="計算中..."
              >
                重新計算
              </Button>

              {recalculatedData && (
                <Box p={4} borderRadius="md" mt={4}>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>
                    計算結果
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text>交易筆數:</Text>
                      <Text fontWeight="semibold">
                        {recalculatedData.transactionCount}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>總鑽石數:</Text>
                      <Text fontWeight="bold" fontSize="xl" color="blue.600">
                        {formatPoints(recalculatedData.totalPoints)}
                      </Text>
                    </HStack>
                    
                    {recalculatedData.typeBreakdown && 
                     Object.keys(recalculatedData.typeBreakdown).length > 0 && (
                      <Box mt={4}>
                        <Text fontWeight="semibold" mb={2}>類型明細:</Text>
                        {getTypeBreakdownTable(recalculatedData.typeBreakdown)}
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeDialog}>關閉</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MainLayout>
  );
}
